import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout, DonationButton, Spinner, ErrorBanner } from '@scoring/frontend-shared';
import { useApiClient } from '../hooks/useApiClient.js';

interface EventSummary {
  id: string;
  name: string;
  date: string;
  location: string;
  status: string;
}

export function EventListPage(): JSX.Element {
  const apiClient = useApiClient();
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.request<{ events: EventSummary[] }>('GET', '/events');
      setEvents(data.events);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void fetchEvents();
  }, [fetchEvents]);

  return (
    <AppLayout footer={<DonationButton />}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Events</h1>
        <Link
          to="/events/new"
          style={{
            padding: '0.5rem 1rem',
            background: 'var(--color-brand-orange, #F48C06)',
            color: 'var(--color-bg-surface, #FFFFFF)',
            borderRadius: 'var(--radius-button, 8px)',
            textDecoration: 'none',
            fontWeight: 500,
          }}
        >
          + Create Event
        </Link>
      </div>

      {error && <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner>}

      {loading && <Spinner label="Loading events…" />}

      {!loading && events.length === 0 && (
        <p style={{ color: 'var(--color-text-secondary, #6B7280)' }}>No events yet. Create your first event to get started.</p>
      )}

      {!loading && events.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}>
          {events.map((event) => (
            <li key={event.id}>
              <div
                style={{
                  padding: '1rem',
                  border: '1px solid var(--color-border, #E5E7EB)',
                  borderRadius: 'var(--radius-card, 12px)',
                }}
              >
                <Link
                  to={`/events/${event.id}`}
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: '1.1rem' }}>{event.name}</strong>
                    <span
                      style={{
                        padding: '0.15rem 0.5rem',
                        borderRadius: '1rem',
                        fontSize: 'var(--font-size-body-sm, 0.875rem)',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        background: getStatusColor(event.status).bg,
                        color: getStatusColor(event.status).text,
                      }}
                    >
                      {event.status}
                    </span>
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>
                    <span>{new Date(event.date).toLocaleDateString()}</span>
                    <span style={{ margin: '0 0.5rem' }}>·</span>
                    <span>{event.location}</span>
                  </div>
                </Link>
                <div style={{ marginTop: 'var(--space-sm, 8px)', paddingTop: 'var(--space-sm, 8px)', borderTop: '1px solid var(--color-border, #E5E7EB)' }}>
                  <a
                    href={`${import.meta.env.VITE_LEADERBOARD_URL || '/leaderboard'}/${event.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: 'var(--font-size-body-sm, 0.875rem)',
                      color: 'var(--color-brand-green, #1B4332)',
                      textDecoration: 'none',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    📊 Leaderboard ↗
                  </a>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </AppLayout>
  );
}

function getStatusColor(status: string): { bg: string; text: string } {
  switch (status) {
    case 'draft': return { bg: '#fff8c5', text: '#6a5300' };
    case 'published': return { bg: '#e6f4ea', text: 'var(--color-brand-green, #1B4332)' };
    case 'active': return { bg: '#dafbe1', text: 'var(--color-brand-green, #1B4332)' };
    case 'complete': return { bg: 'var(--color-bg-page, #FAFBFC)', text: 'var(--color-text-secondary, #6B7280)' };
    case 'closed': return { bg: '#ffebe9', text: 'var(--color-brand-red, #DC2F02)' };
    default: return { bg: 'var(--color-bg-page, #FAFBFC)', text: 'var(--color-text-secondary, #6B7280)' };
  }
}
